import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ResponsiveBar } from '@nivo/bar';
import { GRAPH_STATUSES } from '@Constants/constants';
import { TaskGraphDataPoint } from '../../types';

interface NivoTaskCompletionBarProps {
    data: TaskGraphDataPoint[];
    onSegmentClick: (dataPoint: TaskGraphDataPoint) => void;
}

const statusOrder: string[] = [
    GRAPH_STATUSES.NotApplicable.label,
    GRAPH_STATUSES.Complete.label,
    GRAPH_STATUSES.PastDue.label,
    GRAPH_STATUSES.InProgress.label,
    GRAPH_STATUSES.NotDue.label,
];

const statusColors: Record<string, string> = {
    [GRAPH_STATUSES.NotApplicable.label]: GRAPH_STATUSES.NotApplicable.color,
    [GRAPH_STATUSES.Complete.label]: GRAPH_STATUSES.Complete.color,
    [GRAPH_STATUSES.PastDue.label]: GRAPH_STATUSES.PastDue.color,
    [GRAPH_STATUSES.InProgress.label]: GRAPH_STATUSES.InProgress.color,
    [GRAPH_STATUSES.NotDue.label]: GRAPH_STATUSES.NotDue.color,
};

// Deliberately distinct from every GRAPH_STATUSES color — in particular from NotDue's #CDCCCC,
// which an unrecognized status used to borrow, making it indistinguishable from "Not Due" and
// leaving its white label unreadable on a light fill. Dark enough for white label text.
const UNKNOWN_STATUS_COLOR = '#6c757d';

// `colors`, `onClick` and `tooltip` are handed the computed datum (which carries `id`), whereas
// `labelTextColor` is handed the bar wrapper whose id lives under `data`. Normalize both shapes.
const resolveBarId = (bar: unknown): string => {
    const b = bar as { id?: string | number; data?: { id?: string | number } };
    return String(b.id ?? b.data?.id ?? '');
};

// One decimal only when the value is not whole, so the common case reads "25%" not "25.0%".
// Kept identical to Graph100StackedBar so the hover reads the same as it did before.
const formatPercentage = (percentage: number): string =>
    Number.isFinite(percentage) ? percentage.toFixed(1).replace(/\.0$/, '') : '0';

// Nivo draws its own tooltip inside the chart container, and the container lives inside a
// location tile that is a fixed 78px tall with `overflow: hidden`. A tooltip above a 22px bar
// therefore got sliced off at the tile's top edge. The hover text is rendered here instead —
// portalled to <body> and positioned at the cursor — so no ancestor can clip it.
interface HoverState {
    label: string;
    value: number;
    percentage: number;
    x: number;
    y: number;
}

const TOOLTIP_CURSOR_OFFSET = 14;
// A bar low or far right on the page would otherwise push the tooltip past the viewport. Both
// values are larger than anything this tooltip renders, so the clamp never cuts the text.
const TOOLTIP_MAX_WIDTH = 220;
const TOOLTIP_MAX_HEIGHT = 40;

const NivoTaskCompletionBar = ({ data, onSegmentClick }: NivoTaskCompletionBarProps): ReactElement => {
    const [hover, setHover] = useState<HoverState | null>(null);
    const validPoints = useMemo(() => (Array.isArray(data) ? data.filter((point) => point.value > 0) : []), [data]);

    // Percentages are of every status, not just the rendered ones — but zero-valued statuses
    // add nothing, so summing the visible points gives the same total.
    const total = useMemo(() => validPoints.reduce((sum, point) => sum + point.value, 0), [validPoints]);

    const orderedPoints = useMemo(() => {
        const known = statusOrder.flatMap((statusId) => validPoints.filter((point) => point.id === statusId));
        const unknown = validPoints.filter((point) => !statusOrder.includes(point.id));
        return [...known, ...unknown];
    }, [validPoints]);

    const segments = useMemo(
        () => orderedPoints.map((point, index) => ({ key: `seg_${index}`, point })),
        [orderedPoints]
    );

    const pointBySegmentKey = useMemo(() => {
        return segments.reduce<Record<string, TaskGraphDataPoint>>((acc, segment) => {
            acc[segment.key] = segment.point;
            return acc;
        }, {});
    }, [segments]);

    const keys = useMemo(() => segments.map((segment) => segment.key), [segments]);

    const clearHover = useCallback(() => setHover(null), []);

    // Nivo only forwards enter/leave to consumers (its own onMouseMove goes straight to the
    // built-in tooltip), so the cursor is tracked on the wrapper and the hovered segment on the bar.
    const trackCursor = useCallback((event: React.MouseEvent) => {
        const { clientX, clientY } = event;
        setHover((current) => (current ? { ...current, x: clientX, y: clientY } : current));
    }, []);

    const chartData = useMemo(() => {
        if (keys.length === 0) return [];

        return [
            keys.reduce<Record<string, number | string>>((acc, key) => {
                const point = pointBySegmentKey[key];
                if (point) acc[key] = point.value;
                return acc;
            }, { bucket: 'tasks' }),
        ];
    }, [keys, pointBySegmentKey]);

    const onBarEnter = (barId: string, barValue: number, event: React.MouseEvent) => {
        const point = pointBySegmentKey[barId];
        setHover({
            label: point?.id ?? barId,
            value: barValue,
            percentage: total > 0 ? (barValue / total) * 100 : 0,
            x: event.clientX,
            y: event.clientY,
        });
    };

    if (chartData.length === 0) {
        return <div className='nivo-task-completion-bar__empty' />;
    }

    return (
        <div className='nivo-task-completion-bar' onMouseMove={trackCursor} onMouseLeave={clearHover}>
            <div className='nivo-task-completion-bar__clip'>
                <ResponsiveBar
                    data={chartData}
                    keys={keys}
                    indexBy='bucket'
                    layout='horizontal'
                    margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    padding={0}
                    innerPadding={0}
                    groupMode='stacked'
                    animate={false}
                    enableGridX={false}
                    enableGridY={false}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={null}
                    axisLeft={null}
                    label={(bar) => `${bar.value}`}
                    labelSkipWidth={16}
                    labelSkipHeight={10}
                    labelTextColor={(bar) => {
                        const point = pointBySegmentKey[resolveBarId(bar)];
                        return point?.id === GRAPH_STATUSES.NotDue.label ? '#2f3b4a' : '#ffffff';
                    }}
                    colors={(bar) => {
                        const point = pointBySegmentKey[resolveBarId(bar)];
                        return (point && statusColors[point.id]) || UNKNOWN_STATUS_COLOR;
                    }}
                    borderRadius={0}
                    isInteractive
                    role='img'
                    ariaLabel='Task completion status breakdown'
                    onClick={(bar) => {
                        const point = pointBySegmentKey[resolveBarId(bar)];
                        if (point) onSegmentClick(point);
                    }}
                    onMouseEnter={(bar, event) => onBarEnter(resolveBarId(bar), Number(bar.value), event)}
                    onMouseLeave={clearHover}
                    // Rendered by the portal below; nivo's own tooltip node is emptied out and
                    // hidden through the theme so nothing is drawn inside the clipped tile.
                    tooltip={() => <></>}
                    theme={{
                        labels: {
                            text: {
                                fontSize: 12,
                                fontWeight: 700,
                            },
                        },
                        tooltip: {
                            container: { display: 'none' },
                            wrapper: { display: 'none' },
                        },
                    }}
                />
            </div>
            {hover && createPortal(
                <div
                    className='nivo-task-completion-bar__tooltip'
                    role='tooltip'
                    style={{
                        left: Math.max(
                            0,
                            Math.min(hover.x + TOOLTIP_CURSOR_OFFSET, window.innerWidth - TOOLTIP_MAX_WIDTH)
                        ),
                        top: Math.max(
                            0,
                            Math.min(hover.y + TOOLTIP_CURSOR_OFFSET, window.innerHeight - TOOLTIP_MAX_HEIGHT)
                        ),
                    }}
                >
                    <strong>{hover.label}</strong>: {formatPercentage(hover.percentage)}% ({hover.value})
                </div>,
                document.body
            )}
        </div>
    );
};

export default NivoTaskCompletionBar;
